/**
 * Coffee Bean Clicker v2 - UI Helper Functions
 */

import * as PIXI from 'pixi.js';
import { THEME, LAYOUT } from './theme.js';

/** Draw a glassmorphism card */
export function drawGlassCard(
  gfx: PIXI.Graphics, x: number, y: number, w: number, h: number,
  radius: number = LAYOUT.cardRadius, alpha: number = THEME.bg.cardAlpha,
): void {
  gfx.beginFill(THEME.bg.glass, alpha);
  gfx.drawRoundedRect(x, y, w, h, radius);
  gfx.endFill();
  gfx.lineStyle(1, THEME.border.glass, THEME.border.glassAlpha);
  gfx.drawRoundedRect(x, y, w, h, radius);
  gfx.lineStyle(0);
}

/** Draw a gold-bordered card */
export function drawGoldCard(
  gfx: PIXI.Graphics, x: number, y: number, w: number, h: number,
  radius = LAYOUT.cardRadius,
): void {
  gfx.beginFill(THEME.accent.gold, 0.06);
  gfx.drawRoundedRect(x, y, w, h, radius);
  gfx.endFill();
  gfx.lineStyle(1, THEME.border.gold, THEME.border.goldAlpha);
  gfx.drawRoundedRect(x, y, w, h, radius);
  gfx.lineStyle(0);
}

/** Draw a pill-shaped button */
export function drawPillButton(
  gfx: PIXI.Graphics, x: number, y: number, w: number, h: number,
  color: number, alpha = 1,
): void {
  gfx.beginFill(color, alpha);
  gfx.drawRoundedRect(x, y, w, h, h / 2);
  gfx.endFill();
}

/** Draw a circular icon background */
export function drawIconCircle(
  gfx: PIXI.Graphics, cx: number, cy: number, radius: number,
  color: number = 0x000000, alpha: number = 0.25,
): void {
  gfx.beginFill(color, alpha);
  gfx.drawCircle(cx, cy, radius);
  gfx.endFill();
}

/** Draw a rounded icon box */
export function drawIconBox(
  gfx: PIXI.Graphics, x: number, y: number, size: number = LAYOUT.iconSize,
  color: number = 0x000000, alpha: number = 0.25,
): void {
  gfx.beginFill(color, alpha);
  gfx.drawRoundedRect(x, y, size, size, 10);
  gfx.endFill();
}

/** Draw a progress bar */
export function drawProgressBar(
  gfx: PIXI.Graphics, x: number, y: number, w: number, h: number,
  progress: number, bgColor: number = 0x000000, fgColor: number = THEME.accent.green,
  bgAlpha: number = 0.3, radius: number = h / 2,
): void {
  // Background
  gfx.beginFill(bgColor, bgAlpha);
  gfx.drawRoundedRect(x, y, w, h, radius);
  gfx.endFill();
  // Fill
  const fillW = Math.max(0, Math.min(1, progress)) * w;
  if (fillW > 0) {
    gfx.beginFill(fgColor, 0.8);
    gfx.drawRoundedRect(x, y, fillW, h, radius);
    gfx.endFill();
  }
}

/** Draw a heat gauge (for roasting minigame) */
export function drawHeatGauge(
  gfx: PIXI.Graphics, x: number, y: number, w: number, h: number,
  heatLevel: number,
): void {
  const radius = 8;

  // Background
  gfx.beginFill(0x000000, 0.4);
  gfx.drawRoundedRect(x, y, w, h, radius);
  gfx.endFill();

  // Zone colors (from bottom to top)
  const zones = [
    { start: 0,    end: 0.30, color: 0x90CAF9 },  // raw - blue
    { start: 0.30, end: 0.50, color: 0xFFEB3B },  // light - yellow
    { start: 0.50, end: 0.70, color: 0xFF9800 },  // medium - orange
    { start: 0.70, end: 0.85, color: 0xF44336 },  // dark - red
    { start: 0.85, end: 0.90, color: 0xD4AF37 },  // perfect - gold
    { start: 0.90, end: 1.00, color: 0x424242 },  // burnt - dark gray
  ];

  for (const z of zones) {
    const zy = y + h * (1 - z.end);
    const zh = h * (z.end - z.start);
    gfx.beginFill(z.color, 0.3);
    gfx.drawRect(x + 2, zy, w - 4, zh);
    gfx.endFill();
  }

  // Heat indicator line
  const lineY = y + h * (1 - heatLevel);
  gfx.lineStyle(3, 0xFFFFFF, 0.9);
  gfx.moveTo(x, lineY);
  gfx.lineTo(x + w, lineY);
  gfx.lineStyle(0);

  // Border
  gfx.lineStyle(1, THEME.border.glass, 0.2);
  gfx.drawRoundedRect(x, y, w, h, radius);
  gfx.lineStyle(0);
}

/** Create a PIXI.Text with default style */
export function makeText(
  text: string, size: number, color: number = THEME.text.primary,
  opts: Partial<PIXI.ITextStyle> = {},
): PIXI.Text {
  return new PIXI.Text(text, {
    fontFamily: THEME.font.family,
    fontSize: size,
    fill: color,
    ...opts,
  });
}

/** Create a separator line */
export function makeSeparator(width: number, y: number): PIXI.Graphics {
  const gfx = new PIXI.Graphics();
  gfx.lineStyle(1, THEME.border.glass, 0.05);
  gfx.moveTo(0, y);
  gfx.lineTo(width, y);
  return gfx;
}
